/**
 * 일일보고 업무 항목 ↔ 이슈·칸반·하달 제목 유사도 (연결 제안용)
 * - 괄호·기호·공백 제거 후 소문자, 문자 2-gram 집합의 다이스 계수
 * - 한국어 제목은 띄어쓰기가 들쭉날쭉해 토큰보다 문자 n-gram 이 안정적
 * desktop/src/renderer/src/lib/workItemMatch.ts 와 같은 파일(바꾸면 두 곳)
 */

export interface MatchCandidate {
  type: string;
  id: string;
  title: string;
  subtitle?: string | null;
}

const STOP = /[\s\[\]\(\)\{\}<>「」『』【】_\-–—·•:;,.!?~'"`/\\|*+#@&%$^=]/g;

export function normalizeTitle(s: string): string {
  return (s || "").toLowerCase().replace(STOP, "");
}

function bigrams(s: string): Set<string> {
  const out = new Set<string>();
  if (s.length < 2) {
    if (s.length === 1) out.add(s);
    return out;
  }
  for (let i = 0; i < s.length - 1; i++) out.add(s.slice(i, i + 2));
  return out;
}

export function titleSimilarity(a: string, b: string): number {
  const na = normalizeTitle(a);
  const nb = normalizeTitle(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  // 한쪽이 다른 쪽을 포함하면 높은 점수 (짧은 쪽 기준 보정)
  if (na.length >= 4 && (nb.includes(na) || na.includes(nb))) {
    const shorter = Math.min(na.length, nb.length);
    const longer = Math.max(na.length, nb.length);
    return Math.max(0.6, shorter / longer);
  }
  const ga = bigrams(na);
  const gb = bigrams(nb);
  let inter = 0;
  ga.forEach((g) => {
    if (gb.has(g)) inter++;
  });
  return (2 * inter) / (ga.size + gb.size);
}

export const SUGGEST_THRESHOLD = 0.34;

/** 입력/항목 제목과 비슷한 후보 상위 n개 (점수 내림차순, 임계값 이상만) */
export function suggestLinks<T extends MatchCandidate>(
  title: string,
  candidates: T[],
  limit = 3,
  threshold = SUGGEST_THRESHOLD
): Array<T & { score: number }> {
  if (!title || normalizeTitle(title).length < 2) return [];
  return candidates
    .map((c) => ({ ...c, score: titleSimilarity(title, c.title) }))
    .filter((c) => c.score >= threshold)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}
